import { Controller } from "@hotwired/stimulus"

// Bulk selection of the configurable lists with bulk actions — Stundenzettel, Krankmeldungen,
// Urlaubsanträge (configurable_time_sheets/_table, sick_notes/_table, leave_requests/_table).
//
// Every entry is rendered twice — a row of the desktop table and a card for phones — and each
// carries its own ids[] checkbox. Select-all, shift-click ranges and the count act on the visible
// set; every change is mirrored onto the hidden twin (same value), because both sit inside the
// bulk form and submit. Otherwise ticks made before crossing the sm breakpoint (rotating a phone)
// would stay invisible yet still be deleted or approved by the next bulk action.
export default class extends Controller {
  static targets = ["selectAll", "checkbox", "selectedCount", "selectionHeader", "mobileBar"]
  static values = { keepReplacedTicks: Boolean }

  connect() {
    this.lastChecked = null
    this.replacedTicks = new Map()
    this.updateHeaderVisibility()
  }

  // Opt-in (keep-replaced-ticks): a row a turbo stream replaces — an inline cell save answers
  // with the whole row — comes back with its checkbox as rendered, unticked, and the bulk bar
  // would skip it while the count still named it. Stimulus reports the old checkbox gone before
  // the new one arrives, in the same mutation batch, so the tick is handed over by value and
  // forgotten once that batch is done. Not the default: lists that re-render their rows after a
  // bulk action mean the selection to end there.
  checkboxTargetDisconnected(checkbox) {
    if (!this.keepReplacedTicksValue || !checkbox.checked) return

    this.replacedTicks.set(checkbox.value, (this.replacedTicks.get(checkbox.value) || 0) + 1)
    queueMicrotask(() => this.replacedTicks.delete(checkbox.value))
  }

  checkboxTargetConnected(checkbox) {
    const count = this.replacedTicks?.get(checkbox.value)
    if (!count) return

    this.replacedTicks.set(checkbox.value, count - 1)
    checkbox.checked = true
    this.updateSelectAllState()
    this.updateHeaderVisibility()
  }

  // A hidden element (display: none, here via the sm breakpoint) has no offsetParent.
  get visibleCheckboxes() {
    return this.checkboxTargets.filter((cb) => cb.offsetParent !== null)
  }

  toggleAll(event) {
    const checked = event.target.checked
    this.visibleCheckboxes.forEach((cb) => {
      cb.checked = checked
    })
    this.syncTwins()
    this.updateHeaderVisibility()
  }

  toggle(event) {
    const checkboxes = this.visibleCheckboxes

    // Shift-click for range selection
    if (event.shiftKey && this.lastChecked && checkboxes.includes(this.lastChecked)) {
      const start = checkboxes.indexOf(this.lastChecked)
      const end = checkboxes.indexOf(event.target)
      const range = [start, end].sort((a, b) => a - b)

      for (let i = range[0]; i <= range[1]; i++) {
        checkboxes[i].checked = event.target.checked
      }
    }

    this.lastChecked = event.target
    this.syncTwins()
    this.updateSelectAllState()
    this.updateHeaderVisibility()
  }

  // The visible set is authoritative; copy its state onto the hidden checkbox of the same entry.
  syncTwins() {
    const checkedByValue = new Map(this.visibleCheckboxes.map((cb) => [cb.value, cb.checked]))
    this.checkboxTargets.forEach((cb) => {
      if (checkedByValue.has(cb.value)) cb.checked = checkedByValue.get(cb.value)
    })
  }

  updateSelectAllState() {
    if (!this.hasSelectAllTarget) return

    const checkboxes = this.visibleCheckboxes
    const total = checkboxes.length
    const checked = checkboxes.filter((cb) => cb.checked).length

    this.selectAllTarget.checked = checked === total && total > 0
    this.selectAllTarget.indeterminate = checked > 0 && checked < total
  }

  updateHeaderVisibility() {
    const checkedCount = this.visibleCheckboxes.filter((cb) => cb.checked).length
    const hasSelection = checkedCount > 0

    // The desktop bar and the mobile sheet carry the same menu; each is breakpoint-gated itself.
    this.selectionHeaderTargets.forEach((el) => el.classList.toggle("hidden", !hasSelection))
    this.mobileBarTargets.forEach((el) => el.classList.toggle("hidden", !hasSelection))

    this.selectedCountTargets.forEach((el) => {
      el.textContent = checkedCount
    })
  }
}
